import type { RouteLocationNormalized } from 'vue-router';

import { toCamelCase } from '~~/utils';

/**
 * Campaign parameters stored as key-value pairs
 */
export type AdCampaignCookie = Record<string, string>;

/** Supported campaign parameter keys in URLs */
export const campaignParams: string[] = [
	'ad_campaign_id',
	'ad_set_id',
	'ad_id',
	'ad_name',
	'ad_set',
	'ad_campaign',
	'utm_id',
	'utm_term',
	'utm_content',
	'utm_campaign',
	'utm_source',
	'utm_medium'
];

/** Mapping of UTM parameters to their normalized campaign parameter names */
export const paramMapping = {
	utm_id: 'ad_campaign_id',
	utm_source: 'ad_set_id',
	utm_medium: 'ad_id',
	utm_term: 'ad_name',
	utm_content: 'ad_set',
	utm_campaign: 'ad_campaign'
} as const;

/**
 * Check if a route contains any recognized campaign parameters
 * This function is safe to use in middleware as it doesn't use any Vue reactivity
 * @param route - Vue Router route object to check
 * @returns Whether the route contains campaign parameters
 */
export function routeHasCampaignParams(
	route: RouteLocationNormalized
): boolean {
	const { query } = route;
	return campaignParams.some(key => key in query);
}

/**
 * Extract and normalize campaign parameters from a route
 * This function is safe to use in middleware as it doesn't use any Vue reactivity
 * @param route - Vue Router route object containing query parameters
 * @returns Normalized campaign parameters
 */
export function getCampaignParamsFromRoute(
	route: RouteLocationNormalized
): AdCampaignCookie {
	const { query } = route;
	const campaignData: AdCampaignCookie = {};

	for (const key of Object.keys(query)) {
		if (!campaignParams.includes(key) || typeof query[key] !== 'string')
			continue;

		if (!key.startsWith('utm_')) {
			campaignData[toCamelCase(key)] = query[key] as string;
			continue;
		}

		const mappedKey = paramMapping[key as keyof typeof paramMapping];
		campaignData[toCamelCase(mappedKey)] = query[key] as string;
	}

	return campaignData;
}

/**
 * Set campaign parameters in sessionStorage
 * This function is safe to use in middleware as it directly accesses sessionStorage
 * @param campaignParams - Campaign parameters to store
 */
export function setAdCampaignInStorage(campaignParams: AdCampaignCookie): void {
	if (typeof window === 'undefined') return;

	try {
		sessionStorage.setItem('ad_campaign', JSON.stringify(campaignParams));
	} catch (error) {
		console.error('Failed to save campaign parameters:', error);
	}
}

/**
 * Get campaign parameters from sessionStorage
 * This function is safe to use in middleware as it directly accesses sessionStorage
 * @returns Campaign parameters from storage or empty object
 */
export function getAdCampaignFromStorage(): AdCampaignCookie {
	if (typeof window === 'undefined') return {};

	try {
		const stored = sessionStorage.getItem('ad_campaign');
		return stored ? JSON.parse(stored) : {};
	} catch (error) {
		console.error('Failed to load campaign parameters:', error);
		return {};
	}
}

/**
 * Check if there are active campaign parameters in sessionStorage
 * This function is safe to use in middleware as it directly accesses sessionStorage
 * @returns Whether there are campaign parameters in storage
 */
export function hasCampaignInStorage(): boolean {
	const campaignData = getAdCampaignFromStorage();
	return Object.keys(campaignData).length > 0;
}
